import type { FunctionComponent, RefObject } from 'preact';

import type { TemplateFunction } from 'Components/types';
import {
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useSelector } from 'Core/store';
import type { Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { getUriFromRequest } from 'Modules/converter/index';
import type { ServerModel, VerifyFitsValue } from 'Modules/serverApi/types';
import { cloneSafe, type CloneSafeProps } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';

import type { VehicleTemplateData } from '../types';

export type Params = {
  template: string;
  isVehicleSelected: boolean;
  fitmentSearchTitle: string;
  fits: VerifyFitsValue | undefined;
  productData: ServerModel.SearchItem;
  changeVehicle: () => void;
  discardVehicle: () => void;
  getVehicleUrl: (pathname: string) => URL;
  openDialog: () => void;
} & VehicleTemplateData;

type Props = {
  template: TemplateFunction<Params>;
  rootRef: RefObject<HTMLElement>;
  templateName: string;
  appendedClasses: CloneSafeProps['appendedClasses'];
  isVehicleSelected: boolean;
  responseVehicle: Vehicle;
  verifyFitmentProductData: ServerModel.SearchItem;
  discardVehicle: () => void;
  onChangeVehicle: () => void;
  openDialog: () => void;
};

const VerifyFitmentLabel: FunctionComponent<Props> = ({
  template,
  rootRef,
  templateName,
  appendedClasses,
  isVehicleSelected,
  responseVehicle,
  verifyFitmentProductData,
  discardVehicle: discardVehicleProp,
  onChangeVehicle,
  openDialog,
}) => {
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const discardVehicle = () => {
    discardVehicleProp();

    onChangeVehicle();
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const getVehicleUrl = (pathname: string) =>
    getUriFromRequest({ selection: responseVehicle.selection }, { pathname, goToSearchPage: !pathname });

  const params = {
    template: templateName,
    isVehicleSelected,
    fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
    fits: verifyFitmentProductData.fitsTheVehicle,
    productData: verifyFitmentProductData,
    vehicle: responseVehicle.filteredFieldMap,
    vehicleString: responseVehicle.toString(),
    vehicleBaseValues: responseVehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: responseVehicle.getFilteredValues(extraFitmentFields),
    changeVehicle: onChangeVehicle,
    discardVehicle,
    getVehicleUrl,
    openDialog,
  };
  const component = safeTemplateCall(template, params, 'VerifyFitmentLabel');

  return cloneSafe(component, rootRef, { appendedClasses });
};

export default VerifyFitmentLabel;
